// v2 cockpit — the VERDICT row (owner, 2026-10-22): three coloured tiles, worst
// first, each « one bold sentence + one detail line + a link », so anybody
// looking at the home knows how to react. The worker decides (lib/homeVerdict.ts
// buildVerdict); this file only draws. « #home-todo » / « #home-pipeline »
// scroll to a block of this page instead of navigating.
import { Link } from 'react-router-dom';
import type { VerdictTile } from '@/lib/dashboardApi';
import { useT } from '@/lib/i18n';

type TFn = ReturnType<typeof useT>['t'];

const TONE = {
  red: { box: 'bg-status-stuck-bg border-status-stuck-border', ink: 'text-status-stuck' },
  amber: { box: 'bg-status-waiting-bg border-status-waiting-border', ink: 'text-status-waiting' },
  green: { box: 'bg-status-done-bg border-status-done-border', ink: 'text-status-done' },
} as const;

const ICON: Record<VerdictTile['kind'], string> = {
  waiting: '⏱', loss: '↘', adsStale: '⟳', aiSpike: '↗', ordersDrop: '↘', stuckParcels: '▣', albumsLate: '⏱', aiGap: '≠',
  profitable: '✓', nothingWaiting: '✓', ordersUp: '↗', ordersStable: '✓', productionOnTime: '✓', noStuckParcels: '✓', allGood: '✓',
};

/** English sentences for the worker's French ones (French shows the worker's text as is). */
function englishText(v: VerdictTile, t: TFn): { title: string; detail: string } {
  const p = v.params;
  switch (v.kind) {
    case 'waiting': return { title: Number(p.n) === 1 ? t('1 client attend > 1 h') : t('{n} clients attendent > 1 h', { n: p.n }), detail: String(p.who ?? v.detail) };
    case 'loss': return { title: t('Profit négatif : {x} DH', { x: p.x }), detail: t('encaissé {r} DH · pub {a} DH', { r: p.revenue, a: p.ads }) };
    case 'adsStale': return { title: t('Coût pub : synchro en panne'), detail: t('dernière synchro {d} · profit net inconnu', { d: p.date }) };
    case 'ordersDrop': return { title: t('Commandes −{x} % sur la période', { x: p.x }), detail: t('{c} nouvelles vs {p}', { c: p.cur, p: p.prev }) };
    case 'stuckParcels': return { title: t('{n} colis bloqués', { n: p.n }), detail: t('{who} sans mouvement AMEEX', { who: p.who }) };
    case 'albumsLate': return { title: t('{n} albums en attente > 48 h', { n: p.n }), detail: String(p.where ?? v.detail) };
    case 'aiSpike': return { title: t("Coût IA : {x} DH aujourd'hui", { x: p.x }), detail: t('×{r} la moyenne sur 7 jours ({a} DH/jour)', { r: p.r, a: p.avg }) };
    case 'aiGap': return p.provider === 'claude'
      // The daily Claude check (2026-10-23): our Claude total vs Anthropic's.
      ? { title: t('Coût IA : écart {x} % avec Claude', { x: p.x }), detail: `${Number(p.more) ? t('des appels ne sont pas comptés') : t('nous comptons plus que Claude')} · ${p.date}` }
      : { title: t('Coût IA : écart {x} % avec OpenRouter', { x: p.x }), detail: `${Number(p.more) ? t('des appels ne sont pas comptés') : t('nous comptons plus qu’OpenRouter')} · ${p.date}` };
    case 'profitable': return { title: t('Rentable : {x} DH de profit', { x: p.x }), detail: t('après pub, IA, fabrication et livraison') };
    case 'ordersUp': return { title: t('Commandes +{x} % sur la période', { x: p.x }), detail: t('{c} nouvelles vs {p}', { c: p.cur, p: p.prev }) };
    case 'ordersStable': return { title: t('Commandes stables'), detail: t('{c} nouvelles vs {p}', { c: p.cur, p: p.prev }) };
    case 'nothingWaiting': return { title: t('Rien en attente'), detail: t('personne n’attend de réponse') };
    case 'productionOnTime': return { title: t('Production à l’heure'), detail: t('aucun album en attente > 48 h') };
    case 'noStuckParcels': return { title: t('Livraisons fluides'), detail: t('aucun colis bloqué chez AMEEX') };
    default: return { title: t('Tout va bien'), detail: t('rien à signaler') };
  }
}

function scrollToBlock(hash: string) {
  document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function Tile({ v }: { v: VerdictTile }) {
  const { t, lang } = useT();
  const tone = TONE[v.tone];
  const text = lang === 'en' ? englishText(v, t) : { title: v.title, detail: v.detail };
  const inner = (
    <>
      <span aria-hidden className={`w-[34px] h-[34px] rounded-[10px] grid place-items-center text-[17px] shrink-0 bg-white ${tone.ink}`}>{ICON[v.kind]}</span>
      <span className="min-w-0 flex-1">
        <b className={`block text-[14px] font-bold tracking-[-0.005em] leading-snug lg:truncate ${tone.ink}`} title={text.title}>{text.title}</b>
        <small className="block text-[12px] mt-px text-adm-ink-2 line-clamp-2 lg:line-clamp-none lg:truncate" title={text.detail}>{text.detail}</small>
      </span>
      <span className={`ml-auto pl-1 text-[12px] font-semibold whitespace-nowrap ${tone.ink}`}>{t(v.cta)} →</span>
    </>
  );
  const cls = `flex items-center gap-3 rounded-xl border px-3.5 py-3 min-w-0 transition-[filter] hover:brightness-[0.98] ${tone.box}`;
  if (v.href.startsWith('#')) {
    return <button type="button" data-verdict={v.kind} onClick={() => scrollToBlock(v.href)} className={`${cls} text-left w-full`}>{inner}</button>;
  }
  return <Link to={v.href} data-verdict={v.kind} className={cls}>{inner}</Link>;
}

export function HomeVerdictRow({ tiles, isLoading }: { tiles?: VerdictTile[]; isLoading: boolean }) {
  if (isLoading || !tiles) {
    return (
      <section aria-busy className="grid grid-cols-1 lg:grid-cols-3 gap-2 lg:gap-2.5">
        {[0, 1, 2].map((i) => <div key={i} className="h-[60px] rounded-xl bg-adm-surface-3 animate-pulse" />)}
      </section>
    );
  }
  return (
    <section aria-label="Verdict" className="grid grid-cols-1 lg:grid-cols-3 gap-2 lg:gap-2.5">
      {tiles.slice(0, 3).map((v) => <Tile key={v.kind} v={v} />)}
    </section>
  );
}
